import { Col, Divider, Drawer, message, Row, Space, Table, Typography } from 'antd';
import { drawerWidth750 } from '../../../utils/constants';
import { useTranslation } from 'react-i18next';
import TemplateServices from '../../../services/templateServices';
import { useEffect, useState } from 'react';
import Loader from '../../../components/common/Loader';
import { useLoader } from '../../../context/loaderContext';
import dayjs from 'dayjs';
import { TemplateField, TemplateObjectDetails } from './templates.types';

const { Text } = Typography;

const ViewTemplate = ({ open, onClose, record }: { open: boolean, onClose: () => void, record: { id: string } }) => {
    const { t } = useTranslation();
    const { loading, setLoading } = useLoader();
    const [templateData, setTemplateData] = useState<TemplateObjectDetails | null>(null);

    useEffect(() => {
        if (!record) return;
        getTemplateDetails(record.id)
    }, []);

    const getTemplateDetails = async (templateId: string) => {
        setLoading(true);
        try {
            const respData = await TemplateServices.getTemplateDetails(templateId);
            if (respData.success) {
                let result = respData.responseObject.value as TemplateObjectDetails;
                setTemplateData(result);

            } else {
                message.error(t('messages.errorAccured'));
            }
        } catch (error) {
            message.error(t('messages.errorAccured'));
        } finally {
            setLoading(false);

        }
    };

    const columns = [
        {
            title: `${t('template.sourceField')} (${t('template.excelFile')})`,
            dataIndex: 'excelField',
            key: 'excelField',
        },
        {
            title: `${t('template.destinationField')} (${t('template.database')})`,
            dataIndex: 'dbField',
            key: 'dbField',
        },
        {
            title: `${t('general.required')}`,
            dataIndex: 'isRequired',
            key: 'isRequired',
            render: (_:any, record:TemplateField) => {
                return (
                    record.isRequired ? (
                        <Text strong style={{ color: '#04c104' }}>{t('general.yes')}</Text>
                    ) : (
                        <Text strong style={{ color: '#616161' }}>{t('general.no')}</Text>
                    )
                );
            },
        },
    ];

    return (
        <Drawer
            title={t('template.viewTemplate')}
            width={drawerWidth750}
            onClose={onClose}
            destroyOnClose
            open={open}
        >
            {loading && (<Loader />)}

            {(templateData && !loading) && (
                <>
                    <Row gutter={30}>
                        <Col xs={24} sm={12} md={12} lg={12}>
                            <Space direction='horizontal' style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text>{t('template.templateName')}:</Text>
                                <Text strong>{templateData.templateDTO?.templateName || ''}</Text>
                            </Space>
                            <Divider style={{ margin: 2 }} variant='dashed' />
                            <Space direction='horizontal' style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text>{t('order.salesOrganization')}:</Text>
                                <Text strong>{templateData.templateDTO?.salesOrganizationCode || ''}</Text>
                            </Space>
                            <Divider style={{ margin: 2 }} variant='dashed' />
                            <Space direction='horizontal' style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text>{t('users.country')}:</Text>
                                <Text strong>{templateData.templateDTO?.countryName || ''}</Text>
                            </Space>
                            <Divider style={{ margin: 2 }} variant='dashed' />
                            <Space direction='horizontal' style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text>{t('order.soldTo')}:</Text>
                                <Text strong>{templateData.templateDTO?.soldToPartyCode || ''}</Text>
                            </Space>
                            <Divider style={{ margin: 2 }} variant='dashed' />
                            <Space direction='horizontal' style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text>{t('order.shipTo')}:</Text>
                                <Text strong>{templateData.templateDTO?.shipToPartyCode || ''}</Text>
                            </Space>
                            <Divider style={{ margin: 2 }} variant='dashed' />
                            <Space direction='horizontal' style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text>{t('template.rpaTemplateType')}:</Text>
                                <Text strong>{templateData.templateDTO?.rpaTemplateTypeDescription || ''}</Text>
                            </Space>
                        </Col>

                        <Col xs={24} sm={12} md={12} lg={12} style={{ borderLeft: "1px solid #e7e7e7" }}>
                            <Space direction='horizontal' style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text>{t('order.orderType')}:</Text>
                                <Text strong>{templateData.templateDTO?.orderTypeCode || ''}</Text>
                            </Space>
                            <Divider style={{ margin: 2 }} variant='dashed' />
                            <Space direction='horizontal' style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text>{t('order.purchaseOrderType')}:</Text>
                                <Text strong>{templateData.templateDTO?.purchasedOrderTypeCode || ''}</Text>
                            </Space>
                            <Divider style={{ margin: 2 }} variant='dashed' />
                            <Space direction='horizontal' style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text>{t('order.distributionChannel')}:</Text>
                                <Text strong>{templateData.templateDTO?.distributionChannelCode || ''}</Text>
                            </Space>
                            <Divider style={{ margin: 2 }} variant='dashed' />
                            <Space direction='horizontal' style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text>{t('template.createdOn')}:</Text>
                                <Text strong>{dayjs.utc(templateData.templateDTO?.createdOn).tz(dayjs.tz.guess()).format('DD/MM/YYYY') || ''}</Text>
                            </Space>
                            <Divider style={{ margin: 2 }} variant='dashed' />
                            <Space direction='horizontal' style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Text>{t('template.createdBy')}:</Text>
                                <Text strong>{templateData.templateDTO?.createdByName || ''}</Text>
                            </Space>
                        </Col>
                    </Row>

                    <Divider />

                    <Row gutter={[16, 16]}>
                        <Col span={24}>
                            <Table
                                size='small'
                                dataSource={templateData?.fieldMappings}
                                columns={columns}
                                rowKey="excelField"
                                pagination={false}
                                bordered
                            />
                        </Col>
                    </Row>
                </>
            )}
        </Drawer>
    );
};

export default ViewTemplate;
